import Styles from './AutopilotUpgradeEntryModal.module.scss'

import { Modal, StackModalProps, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import {
  UserProfileCircle,
  UsersProfiles1,
  ColumnVertical1,
  Target2,
  ArrowRight,
  Close,
  Approvals,
  Grid,
  PaidAds,
  Knowledge,
} from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { Templates } from 'blaze-ui/icons/24/Templates'
import CalendarPost from 'blaze-ui/icons/20/CalendarPost'
import { BlazeAI } from 'almanac-ui/icons'
import { Server02 } from 'blaze-ui/icons/16'
import { trackFreemiumModalCompleted } from './trackFreemiumModalCompleted'
import { AutopilotPlansModal } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'
import { PlanLengthSelector } from 'components/Settings/Workspace/AutopilotPlans/components/Form/components/PlanLengthSelector/PlanLengthSelector'
import { useRootStore } from 'stores/RootStore'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { useMemo, useState, useCallback } from 'react'
import { findPlanCreditLimit } from 'components/Settings/Workspace/AutopilotPlans/utils'
import { observer } from 'mobx-react-lite'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { StripePrice } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/types'
import { nameMap } from 'components/Settings/Workspace/AutopilotPlans/components/Plans/components/PlanDetails/PlanDetails'
import { Credits } from 'blaze-ui/icons/20/Credits'

import ColumnHorizontal from 'blaze-ui/icons/20/ColumnHorizontal'
import { PurchaseContentCreditsModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/PurchaseContentCreditsModal'

interface Props extends StackModalProps {
  variant?:
    | 'end_of_trial'
    | 'out_of_credits'
    | 'low_on_credits'
    | 'no_credits'
    | 'invite_team'
    | 'more_credits'
    | 'upgrade_to_growth'
  targetPlanKey?: PaymentPlanKey
  creditsNeeded?: number
  openStrategyLaunchModal?: () => void
  skipRedirect?: boolean
}

export const AutopilotUpgradeEntryModal = observer((props: Props) => {
  const { variant = 'end_of_trial', targetPlanKey, creditsNeeded, openStrategyLaunchModal, skipRedirect } = props
  const { openModal, closeModal } = useModals()
  const { tierUsageStore } = useRootStore()
  const { currentSubscription } = tierUsageStore
  const isVerticalPlanLength = useFlipperFlag('vertical_plan_length')

  let { plan: currentPlan = PaymentPlanKey.BlazeAutopilotStarter } = currentSubscription || {}
  currentPlan =
    currentPlan === PaymentPlanKey.BlazeAutopilotGrowth
      ? PaymentPlanKey.BlazeAutopilotGrowth
      : PaymentPlanKey.BlazeAutopilotStarter
  const planKey = targetPlanKey || currentPlan
  const workspace = useCurrentWorkspace()

  const isGrowthPlan = planKey === PaymentPlanKey.BlazeAutopilotGrowth || variant === 'upgrade_to_growth'
  const creditLimit = findPlanCreditLimit(tierUsageStore.autopilotPlans, planKey)

  const selectedPlan = tierUsageStore.autopilotPlans.find((p) => p.key === planKey)

  const getDefaultPrice = useCallback(() => {
    if (!selectedPlan) return null
    const hasScheduledCancellation = !!currentSubscription?.cancel_at && currentSubscription?.status === 'active'
    const currentPrice = currentSubscription?.prices?.[0] ?? null
    if (hasScheduledCancellation && currentPrice) return currentPrice
    if (currentPrice?.product === selectedPlan.id) return currentPrice
    return selectedPlan.prices[3]
  }, [selectedPlan, currentSubscription])

  const [selectedPrice, setSelectedPrice] = useState<StripePrice | null>(getDefaultPrice())

  const handleUpgrade = () => {
    if (!selectedPlan) return

    trackFreemiumModalCompleted('upgrade')
    openModal(AutopilotPlansModal, {
      initialStep: 'selected',
      initialPlanAndPrice: { plan: selectedPlan, price: undefined },
      skipRedirect,
    })
  }

  const handleSeeAllPlans = () => {
    trackFreemiumModalCompleted('upgrade')
    openModal(AutopilotPlansModal, { skipRedirect })
  }

  const handleDismiss = () => {
    trackFreemiumModalCompleted(null)
    if (workspace) {
      WorkspaceClient.completeSetFreemiumBrandPlan(workspace.id)
    }
    closeModal()
  }

  const handleProceedToCheckout = () => {
    if (!selectedPlan || !selectedPrice) return

    trackFreemiumModalCompleted('upgrade')
    openModal(AutopilotPlansModal, {
      initialStep: 'checkout',
      initialPlanAndPrice: { plan: selectedPlan, price: selectedPrice },
      skipRedirect,
    })
  }

  const isTrialing = currentSubscription?.status === 'trialing'
  const hasScheduledCancellation = !!currentSubscription?.cancel_at && currentSubscription?.status === 'active'
  const trialDaysLeft = currentSubscription?.trial_days_left || 0
  const planName = nameMap[planKey] || (isGrowthPlan ? 'Growth' : 'Starter')

  const modalHeaderTitle = useMemo(() => {
    if (hasScheduledCancellation) {
      return `Reactivate ${planName}`
    }
    if (variant === 'end_of_trial' && !isTrialing) {
      return 'Your trial has ended'
    }
    if (isTrialing && trialDaysLeft > 0) {
      return `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left in your ${planName} Trial`
    }
    return `Upgrade to ${planName}`
  }, [hasScheduledCancellation, variant, isTrialing, trialDaysLeft, planName])

  const starterColor = '#EF6800'
  const growthColor = '#0083E2'

  const header = useMemo(() => {
    if (variant === 'upgrade_to_growth') {
      return `Purchase ${creditsNeeded} more credits to enable this strategy or save money by upgrading to Growth Plan`
    }
    if (variant === 'out_of_credits') {
      return 'Keep your marketing momentum going!'
    }
    if (variant === 'low_on_credits') {
      return "You're getting the hang of this."
    }
    if (variant === 'no_credits') {
      return 'You used every last credit.'
    }
    if (variant === 'more_credits') {
      return 'Upgrade to Growth to get more monthly credits'
    }
    if (variant === 'invite_team') {
      return 'Upgrade to Growth to get your whole team involved.'
    }
    if (variant === 'end_of_trial') {
      return 'Your first week was just the start. Keep your momentum going.'
    }
    return isGrowthPlan
      ? "You've built something worth scaling. Growth gives you the full platform."
      : "You're ready to go. Keep your strategy running with Starter."
  }, [variant, isGrowthPlan, creditsNeeded])

  const subHeader = useMemo(() => {
    if (variant === 'upgrade_to_growth') {
      return `Cover this campaign and still have ${creditLimit.toLocaleString()} credits per month with the Growth plan`
    }
    if (variant === 'out_of_credits') {
      return `You've used your trial credits. Upgrade to keep creating with ${creditLimit.toLocaleString()} fresh credits every month.`
    }
    if (variant === 'low_on_credits') {
      return "You've used most of your trial credits, which means Blaze is working. Upgrade to keep creating."
    }
    if (variant === 'no_credits') {
      return 'That means Blaze is working. Upgrade to keep your momentum going.'
    }
    if (variant === 'invite_team') {
      return 'Everything you set up keeps running. Now add the tools to scale it, including team workflows, advanced AI, and insights that compound.'
    }
    if (variant === 'end_of_trial') {
      return "You've seen what Blaze can do in seven days. Imagine how your marketing will transform in 30, 90, or 365 days. Keep everything running — and keep growing — with Growth."
    }

    return isGrowthPlan
      ? 'Everything you set up keeps running. Now add the tools to scale it, including team workflows, advanced AI, and insights that compound.'
      : 'Everything you built during your trial — your content calendar, your campaigns, your brand voice — stays live and publishing.'
  }, [variant, isGrowthPlan, creditLimit])

  const img = useMemo(() => {
    if (variant === 'out_of_credits' || variant === 'low_on_credits' || variant === 'no_credits') {
      return 'https://res.cloudinary.com/almanac/image/upload/v1769716847/blaze_assets/virtual_marketer/onboarding/credits_upgrade_modal_pujgst.png'
    }

    return isGrowthPlan
      ? 'https://res.cloudinary.com/almanac/image/upload/v1769569791/blaze_assets/virtual_marketer/onboarding/growth_upgrade_modal_pwybni.png'
      : 'https://res.cloudinary.com/almanac/image/upload/v1769569791/blaze_assets/virtual_marketer/onboarding/starter_upgrade_modal_jxawon.png'
  }, [variant, isGrowthPlan])

  const autopilotContentCreditsAddon = tierUsageStore.autopilotAddOns.find(
    (addon) => addon.key === PaymentPlanKey.BlazeAutopilotContentCredits,
  )
  const autopilotContentCreditsPrice = autopilotContentCreditsAddon?.prices[0]

  const leftPanel = (
    <div className={Styles.leftPanel} data-vertical-plan-length={isVerticalPlanLength ? 'on' : 'off'}>
      <div className={Styles.leftPanelContent}>
        <div className={Styles.header}>
          <Heading level={isVerticalPlanLength ? 2 : 1}>{header}</Heading>
          <Text color={isVerticalPlanLength ? 'var(--dark-60)' : 'var(--dark-80)'}>{subHeader}</Text>
        </div>

        <div data-vertical-plan-length={isVerticalPlanLength ? 'on' : 'off'} className={Styles.freePlanSection}>
          <div className={Styles.freePlanSectionLeft}>
            <Heading level={5}>What you get with {isGrowthPlan ? 'Growth' : 'Starter'}:</Heading>
            {planKey === PaymentPlanKey.BlazeAutopilotStarter &&
              variant !== 'upgrade_to_growth' &&
              (isVerticalPlanLength ? (
                <ul data-feature-variant="vertical">
                  <li>
                    <Credits size={20} color={starterColor} />
                    <span>
                      <Text variant="primary">{creditLimit.toLocaleString()} credits/mo</Text>
                      <Text variant="metadata" color="var(--dark-60)">
                        Create consistent, on-brand content every week.
                      </Text>
                    </span>
                  </li>
                  <li>
                    <CalendarPost size={20} color={starterColor} />
                    <span>
                      <Text variant="primary">3 posting accounts</Text>
                      <Text variant="metadata" color="var(--dark-60)">
                        Scheduling and posting automatically, every day.
                      </Text>
                    </span>
                  </li>
                  <li>
                    <ColumnHorizontal size={20} color={starterColor} />
                    <span>
                      <Text variant="primary">Weekly campaigns</Text>
                      <Text variant="metadata" color="var(--dark-60)">
                        Move from random posts to focused weekly results.
                      </Text>
                    </span>
                  </li>
                  <li>
                    <PaidAds size={20} color={starterColor} />
                    <span>
                      <Text variant="primary">Automated ad campaigns</Text>
                      <Text variant="metadata" color="var(--dark-60)">
                        Run paid ads on autopilot.
                      </Text>
                    </span>
                  </li>
                  <li>
                    <Knowledge size={20} color={starterColor} />
                    <span>
                      <Text variant="primary">AI learning loop</Text>
                      <Text variant="metadata" color="var(--dark-60)">
                        The more it publishes for you, the smarter it gets.
                      </Text>
                    </span>
                  </li>
                </ul>
              ) : (
                <Text>
                  <ul>
                    <li>
                      <Templates size={24} color={starterColor} /> A multi-month content strategy that converts.
                    </li>
                    <li>
                      <CalendarPost size={24} color={starterColor} />
                      Scheduling and posting. Automatically, every day.
                    </li>
                    <li>
                      <UserProfileCircle size={24} color={starterColor} />
                      Simple, powerful insights that lead to growth.
                    </li>
                    <li>
                      <BlazeAI size={24} color={starterColor} />
                      Complete control. Full customization.
                    </li>
                    <li>
                      <Server02 size={24} color={starterColor} />
                      {creditLimit.toLocaleString()} AI credits every month.
                    </li>
                  </ul>
                </Text>
              ))}
            {(planKey === PaymentPlanKey.BlazeAutopilotGrowth || variant === 'upgrade_to_growth') &&
              (isVerticalPlanLength ? (
                <ul data-feature-variant="vertical">
                  <li>
                    <Credits size={20} color={growthColor} />
                    <span>
                      <Text variant="primary">{creditLimit.toLocaleString()} credits/mo</Text>
                      <Text variant="metadata" color="var(--dark-60)">
                        Create more high-quality content across every channel.
                      </Text>
                    </span>
                  </li>
                  <li>
                    <CalendarPost size={20} color={growthColor} />
                    <span>
                      <Text variant="primary">10 posting accounts</Text>
                      <Text variant="metadata" color="var(--dark-60)">
                        Reach new audiences on more channels.
                      </Text>
                    </span>
                  </li>
                  <li>
                    <UsersProfiles1 size={20} color={growthColor} />
                    <span>
                      <Text variant="primary">Unlimited users</Text>
                      <Text variant="metadata" color="var(--dark-60)">
                        Invite your whole team to collaborate.
                      </Text>
                    </span>
                  </li>
                  <li>
                    <Grid size={20} color={growthColor} />
                    <span>
                      <Text variant="primary">Yearly planning window</Text>
                      <Text variant="metadata" color="var(--dark-60)">
                        Proactively plan all your campaigns.
                      </Text>
                    </span>
                  </li>
                  <li>
                    <Approvals size={20} color={growthColor} />
                    <span>
                      <Text variant="primary">Approvals</Text>
                      <Text variant="metadata" color="var(--dark-60)">
                        Ensure nothing gets published without explicit approval.
                      </Text>
                    </span>
                  </li>
                </ul>
              ) : (
                <Text>
                  <ul>
                    <li>
                      <Templates size={24} color={growthColor} /> Post on every channel, everywhere.
                    </li>
                    <li>
                      <UsersProfiles1 size={24} color={growthColor} />
                      Bring your whole team with built-in approvals.
                    </li>
                    <li>
                      <ColumnVertical1 size={24} color={growthColor} />
                      Run multiple campaigns simultaneously.
                    </li>
                    <li>
                      <Target2 size={24} color={growthColor} />
                      Get AI that optimizes your paid spend automatically.
                    </li>
                    <li>
                      <Server02 size={24} color={growthColor} />
                      {creditLimit.toLocaleString()} credits/month to create at scale
                    </li>
                  </ul>
                </Text>
              ))}
          </div>
        </div>
      </div>
      {!isVerticalPlanLength && (
        <div className={Styles.buttonContainer}>
          <Button size="lg" onPress={handleSeeAllPlans} aria-label="See all plans" variant="tertiary">
            Compare Plans
          </Button>
          <div className={Styles.inline}>
            <Button size="lg" onPress={handleUpgrade} endIcon={ArrowRight} fullWidth>
              Activate {planKey === PaymentPlanKey.BlazeAutopilotGrowth ? 'Growth' : 'Starter'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )

  if (isVerticalPlanLength) {
    return (
      <Modal.Root onPressOutside={handleDismiss} bodyClassName={Styles.bodyClassName} size="lg">
        <div className={`${Styles.content} ${Styles.contentWithFooter}`}>
          <div className={Styles.closeButtonMobile}>
            <IconButton icon={Close} size="lg" variant="tertiary" onPress={handleDismiss} aria-label="Close" />
          </div>
          <div className={Styles.modalHeader}>
            <Heading level={5}>{modalHeaderTitle}</Heading>
            <IconButton icon={Close} size="lg" variant="tertiary" onPress={handleDismiss} aria-label="Close" />
          </div>
          <div className={Styles.panelsRow}>
            {leftPanel}
            <div className={Styles.rightPanel}>
              {selectedPlan ? (
                <div className={Styles.selectedPlanFormWrapper}>
                  <PlanLengthSelector
                    selectedPlan={selectedPlan}
                    selectedPrice={selectedPrice}
                    onSelectPrice={setSelectedPrice}
                  />
                </div>
              ) : (
                <img src={img} alt="Campaign Dashboard" className={Styles.backgroundImage} />
              )}
            </div>
          </div>
          <div className={Styles.footerDivider} />
          <div className={Styles.sharedFooter}>
            <Button size="lg" onPress={handleSeeAllPlans} aria-label="See all plans" variant="tertiary">
              Compare Plans
            </Button>
            <div className={Styles.footerButtonsRight}>
              {variant === 'upgrade_to_growth' && !isTrialing && (
                <Button
                  variant="tertiary"
                  size="lg"
                  isDisabled={!selectedPrice}
                  onPress={() => {
                    if (!autopilotContentCreditsPrice) return

                    openModal(PurchaseContentCreditsModal, {
                      price: autopilotContentCreditsPrice,
                      onSuccess: openStrategyLaunchModal,
                    })
                  }}
                  color="var(--dark-60)"
                >
                  <div className={Styles.purchaseCreditsButton}>
                    <span>I'll just purchase the credits</span>
                    <Credits size={15} />
                    {creditsNeeded}
                  </div>
                </Button>
              )}
              <Button size="lg" isDisabled={!selectedPrice} onPress={handleProceedToCheckout} endIcon={ArrowRight}>
                {variant === 'upgrade_to_growth' ? 'Proceed with Growth Plan' : 'Proceed to Checkout'}
              </Button>
            </div>
          </div>
        </div>
      </Modal.Root>
    )
  }

  return (
    <Modal.Root onPressOutside={handleDismiss} bodyClassName={Styles.bodyClassName} size="lg">
      <div className={Styles.content}>
        <div className={Styles.closeButtonMobile}>
          <IconButton icon={Close} size="lg" variant="tertiary" onPress={handleDismiss} aria-label="Close" />
        </div>
        {leftPanel}
        <div className={Styles.rightPanel}>
          <img src={img} alt="Campaign Dashboard" className={Styles.backgroundImage} />
          <div className={Styles.closeButton}>
            <IconButton icon={Close} size="lg" variant="tertiary" onPress={handleDismiss} aria-label="Close" />
          </div>
        </div>
      </div>
    </Modal.Root>
  )
})
